import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export type SetupApprovalType = "Pricing" | "CropType" | "CropVariety" | "Input";
export type SetupApprovalStatus = "Pending" | "Approved" | "Rejected";

export interface SetupApprovalRequest {
  id: string;
  approvalType: number;
  approvalTypeName: SetupApprovalType;
  entityJson: string;
  existingEntityId?: string;
  description: string;
  status: number;
  statusName: SetupApprovalStatus;
  requestedByUserId: string;
  requestedByUserName: string;
  requestedAt: string;
  reviewedByUserId?: string;
  reviewedByUserName?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  resubmittedAt?: string;
  createdAt: string;
}

export interface CreateSetupApprovalRequest {
  approvalType: number; // 1=Pricing, 2=CropType, 3=CropVariety, 4=Input
  entityJson: string;
  existingEntityId?: string;
  description: string;
}

export interface ApproveSetupApprovalRequest {
  reviewNotes?: string;
}

export interface RejectSetupApprovalRequest {
  rejectionReason: string;
}

export interface ResubmitSetupApprovalRequest {
  entityJson: string;
  description?: string;
}

export const SETUP_APPROVAL_TYPE = {
  Pricing: 1,
  CropType: 2,
  CropVariety: 3,
  Input: 4,
} as const;

export const setupApprovalApi = {
  submit: async (payload: CreateSetupApprovalRequest): Promise<SetupApprovalRequest> => {
    const { data } = await apiClient.post<ApiResponse<SetupApprovalRequest>>(
      "/setup-approvals",
      payload
    );
    return data.data;
  },

  getPending: async (): Promise<SetupApprovalRequest[]> => {
    const { data } = await apiClient.get<ApiResponse<SetupApprovalRequest[]>>(
      "/setup-approvals/pending"
    );
    return data.data;
  },

  getById: async (id: string): Promise<SetupApprovalRequest> => {
    const { data } = await apiClient.get<ApiResponse<SetupApprovalRequest>>(
      `/setup-approvals/${id}`
    );
    return data.data;
  },

  approve: async (id: string, payload: ApproveSetupApprovalRequest): Promise<SetupApprovalRequest> => {
    const { data } = await apiClient.post<ApiResponse<SetupApprovalRequest>>(
      `/setup-approvals/${id}/approve`,
      payload
    );
    return data.data;
  },

  reject: async (id: string, payload: RejectSetupApprovalRequest): Promise<SetupApprovalRequest> => {
    const { data } = await apiClient.post<ApiResponse<SetupApprovalRequest>>(
      `/setup-approvals/${id}/reject`,
      payload
    );
    return data.data;
  },

  resubmit: async (id: string, payload: ResubmitSetupApprovalRequest): Promise<SetupApprovalRequest> => {
    const { data } = await apiClient.post<ApiResponse<SetupApprovalRequest>>(
      `/setup-approvals/${id}/resubmit`,
      payload
    );
    return data.data;
  },

  getMySubmissions: async (): Promise<SetupApprovalRequest[]> => {
    const { data } = await apiClient.get<ApiResponse<SetupApprovalRequest[]>>(
      "/setup-approvals/my-submissions"
    );
    return data.data;
  },
};
